class JapaHouseIntake extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.extraIndex = 8;
    const options = { signal: this.controller.signal };
    this.form = this.querySelector('[data-house-form]');
    this.form.noValidate = true;
    this.messages = JSON.parse(this.querySelector('[data-validation-messages]').textContent);
    this.addEventListener('change', event => {
      if (event.target.matches('[data-house-size], [data-house-tier]')) this.updateEstimate();
      if (event.target.matches('[data-house-base], [data-house-plaque]')) this.updateBaseOptions();
      if (event.target.matches('[data-photo-method]')) this.updatePhotoMethod();
      if (event.target.matches('input[type="file"]')) this.checkFiles();
      if (event.target.matches('[data-ship-date]')) this.updateShipDate();
      if (this.attempted) this.showErrors(false);
    }, options);
    this.addEventListener('click', event => {
      if (event.target.closest('[data-add-photo]')) this.addPhoto();
      if (event.target.closest('[data-remove-photo]')) {
        event.target.closest('label').remove();
        this.checkFiles();
        if (this.attempted) this.showErrors(false);
      }
    }, options);
    this.form.addEventListener('submit', event => {
      this.attempted = true;
      this.checkFiles();
      this.updateShipDate();
      if (!this.showErrors(true)) { event.preventDefault(); return; }
      const button = this.querySelector('[data-house-submit]');
      if (button) {
        button.disabled = true;
        button.textContent = this.querySelector('[data-uploading]').textContent;
      }
    }, options);
    window.addEventListener('pageshow', () => {
      this.updateShipDate();
      const button = this.querySelector('[data-house-submit]');
      if (button) { button.disabled = false; button.textContent = this.submitLabel; }
    }, options);
    this.submitLabel = this.querySelector('[data-house-submit]')?.textContent;
    this.updateEstimate();
    this.updatePhotoMethod();
    this.updateBaseOptions();
    this.updateShipDate();
  }
  updateShipDate() {
    const field = this.querySelector('[data-ship-date]');
    if (!field) return;
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const part = type => parts.find(item => item.type === type).value;
    const earliest = new Date(Date.UTC(Number(part('year')), Number(part('month')) - 1, Number(part('day'))));
    let remaining = 5;
    while (remaining) {
      earliest.setUTCDate(earliest.getUTCDate() + 1);
      if (![0, 6].includes(earliest.getUTCDay())) remaining--;
    }
    field.min = earliest.toISOString().slice(0, 10);
    field.setCustomValidity('');
    if (field.value && field.value < field.min) field.setCustomValidity(this.messages.date.replace('{date}', field.min));
    const hint = this.querySelector('[data-earliest-date]');
    hint.textContent = this.messages.earliest.replace('{date}', new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', dateStyle: 'long' }).format(earliest));
  }
  disconnectedCallback() { this.controller?.abort(); }
  updateEstimate() {
    const selected = selector => this.querySelector(selector).selectedOptions[0];
    const base = Number(selected('[data-house-size]').dataset.base);
    const tier = selected('[data-house-tier]').dataset.extra;
    const amount = base;
    this.querySelector('[data-house-estimate]').textContent = tier === '' ? '$' + amount + '–$' + (amount + 40) : '$' + (amount + Number(tier));
  }
  updateBaseOptions() {
    const base = this.querySelector('[data-house-base]');
    const plaque = this.querySelector('[data-house-plaque]');
    const wording = this.querySelector('[data-plaque-wording]');
    const houseOnly = base.value === 'House only';
    plaque.disabled = houseOnly;
    if (houseOnly) plaque.value = 'No plaque';
    const showWording = !houseOnly && plaque.value !== 'No plaque';
    this.querySelector('[data-plaque-panel]').hidden = !showWording;
    wording.disabled = !showWording;
  }
  updatePhotoMethod() {
    const folder = this.querySelector('[data-photo-method="folder"]').checked;
    const later = this.querySelector('[data-photo-method="later"]').checked;
    const upload = !folder && !later;
    this.querySelector('[data-later-panel]').hidden = !later;
    this.querySelector('[data-photo-status-property]').value = later ? 'Photos pending' : 'References submitted for review';
    this.querySelector('[data-photo-followup]').disabled = !later;
    this.querySelector('[data-reference-copy]').textContent = later ? this.messages.referenceLater : this.messages.referenceNow;
    this.querySelector('[data-folder-panel]').hidden = !folder;
    const url = this.querySelector('[data-folder-link]');
    url.disabled = !folder;
    url.required = folder;
    this.querySelector('[data-photo-uploads]').hidden = !upload;
    for (const input of this.querySelectorAll('input[type="file"]')) {
      input.disabled = !upload;
      input.required = upload && input.hasAttribute('data-required-photo');
      input.setCustomValidity('');
    }
    this.checkFiles();
  }
  addPhoto() {
    const row = this.querySelector('[data-extra-photo-template]').content.cloneNode(true);
    row.querySelector('input').name = 'properties[Extra reference ' + (++this.extraIndex) + ']';
    this.querySelector('[data-extra-photos]').append(row);
  }
  checkFiles() {
    let total = 0;
    let missing = 0;
    let invalid = false;
    const selected = [];
    const problems = [];
    for (const input of this.querySelectorAll('input[type="file"]')) {
      input.setCustomValidity('');
      if (input.disabled) continue;
      if (input.required && !input.files.length) missing++;
      for (const file of input.files) {
        total += file.size;
        selected.push(input);
        const allowed = input.hasAttribute('data-required-photo') ? /\.(jpe?g|png|webp|heic|heif)$/i : /\.(jpe?g|png|webp|heic|heif|pdf)$/i;
        let message = '';
        if (!allowed.test(file.name)) message = this.messages.fileType;
        else if (!file.size) message = this.messages.emptyFile;
        else if (file.size > 10 * 1024 * 1024) message = this.messages.fileSize;
        if (message) { input.setCustomValidity(message); problems.push(message); invalid = true; }
      }
    }
    if (total > 20 * 1024 * 1024) {
      for (const input of selected) input.setCustomValidity(this.messages.totalSize);
      problems.push(this.messages.totalSize);
      invalid = true;
    }
    if (missing) problems.unshift(this.messages.missingPhotos.replace('{count}', missing));
    const status = this.querySelector('[data-photo-status]');
    const upload = this.querySelector('[data-photo-method="upload"]').checked;
    status.hidden = !upload;
    status.dataset.state = problems.length ? 'warning' : 'ready';
    status.textContent = [...new Set(problems)].join(' ') || this.messages.photosReady;
    return !invalid;
  }
  showErrors(scroll) {
    const controls = [...this.form.querySelectorAll('input,select,textarea')].filter(field => field.willValidate);
    this.querySelectorAll('[data-inline-error]').forEach(node => node.remove());
    const invalid = [];
    controls.forEach((field, index) => {
      field.removeAttribute('aria-invalid');
      const described = (field.getAttribute('aria-describedby') || '').split(' ').filter(id => id && !id.startsWith('jw-field-error-'));
      if (described.length) field.setAttribute('aria-describedby', described.join(' ')); else field.removeAttribute('aria-describedby');
      if (field.validity.valid) return;
      const label = field.closest('label')?.querySelector('span')?.textContent.trim() || field.name;
      const message = field.validity.valueMissing ? this.messages.required : field.validationMessage;
      const error = document.createElement('small');
      error.id = 'jw-field-error-' + index;
      error.dataset.inlineError = '';
      error.textContent = message;
      field.closest('label').append(error);
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', [...described, error.id].join(' '));
      invalid.push({ field, label, message });
    });
    const summary = this.querySelector('[data-error-summary]');
    summary.hidden = !invalid.length;
    const list = summary.querySelector('ul');
    list.replaceChildren();
    invalid.forEach(({ field, label, message }) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label + ': ' + message;
      button.addEventListener('click', () => this.focusError(field));
      item.append(button);
      list.append(item);
    });
    if (scroll && invalid.length) this.focusError(invalid[0].field);
    return !invalid.length;
  }
  focusError(field) {
    field.focus({ preventScroll: true });
    field.closest('label').scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
if (!customElements.get('japa-house-intake')) customElements.define('japa-house-intake', JapaHouseIntake);
